import React, { useEffect, useState } from "react";
import {
  Text,
  useComputedColorScheme,
  useMantineTheme,
  LoadingOverlay,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { FileDto, FolderDto } from "@braoshzmvavz/picnube-common";
import { useAppSelector } from "src/app/hooks";
import { selectCurrentSpace } from "src/state/profile/profileSlice";
import useApi from "src/services/useApi";
import Gallery from "src/components/gallery/Gallery";

export interface GallerySectionProps {
  folderId: string;
  title?: string;
  pageSize?: number;
  enableInfiniteLoading?: boolean;
  isSelectionEnabled?: boolean;
  hiddenOptions?: boolean;
  isMiniGallery?: boolean;
  hardcodeSettings?: any;
}

export default function GallerySection({
  folderId,
  title = "Gallery",
  pageSize = 30,
  enableInfiniteLoading = true,
  isSelectionEnabled = false,
  hiddenOptions = true,
  isMiniGallery = false,
  hardcodeSettings,
}: GallerySectionProps) {
  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`);
  const colorScheme = useComputedColorScheme();
  const currentSpace = useAppSelector(selectCurrentSpace);
  const { fetchFiles } = useApi();

  const [files, setFiles] = useState<FileDto[]>([]);
  const [folders, setFolders] = useState<FolderDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load initial files when component mounts or folderId changes
  useEffect(() => {
    if (!currentSpace || !folderId) return;

    const loadInitialFiles = async () => {
      setIsInitialLoading(true);
      setError(null);
      
      try {
        const response: any = await fetchFiles(currentSpace.id, folderId, 0);
        console.log(response);
        setFiles(response || []);
        setPage(0);
        setHasMore((response?.length || 0) >= pageSize);
      } catch (err) {
        console.error("Error loading initial files:", err);
        setError("Failed to load gallery files");
        setFiles([]);
        setHasMore(false);
      } finally {
        setIsInitialLoading(false);
      }
    };

    loadInitialFiles();
  }, [currentSpace, folderId, pageSize]);

  // Load more files function for infinite scroll
  const loadMore = async () => {
    if (isLoading || !hasMore || !currentSpace) return;

    setIsLoading(true);
    
    try {
      const nextPage = page + 1;
      const response: any = await fetchFiles(currentSpace.id, folderId, nextPage);
      
      if (response && response.length > 0) {
        setFiles(prevFiles => [...prevFiles, ...response]);
        setPage(nextPage);
        setHasMore(response.length >= pageSize);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error("Error loading more files:", err);
      setError("Failed to load more files");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle folder click (empty for now as we're focusing on files)
  const handleFolderClick = (folder: FolderDto) => {
    // This could be implemented to navigate to subfolders if needed
    console.log("Folder clicked:", folder);
  };

  // Handle action clicks (empty for now)
  const handleActionClick = (action: any) => {
    console.log("Action clicked:", action);
  };

  if (error) {
    return (
      <div className="flex flex-col w-full h-full gap-4">
        <Text fz="md" c="dimmed">
          {title}
        </Text>
        <div className="w-full h-full rounded-lg border border-grey-800 overflow-hidden flex items-center justify-center">
          <Text c="red" ta="center">
            {error}
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-full gap-4">
      <div className="w-full h-full overflow-auto">
        {isInitialLoading ? (
          <div className="w-full h-full flex items-center justify-center">
            <LoadingOverlay
              visible={true}
              zIndex={400}
              overlayProps={{
                blur: "xl",
                color: colorScheme === "dark" ? "black" : "black",
                opacity: colorScheme === "dark" ? "70%" : "30%",
              }}
              loaderProps={{ color: "blue", type: "oval", size: "md" }}
            />
          </div>
        ) : (
          <Gallery
            folders={folders}
            files={files}
            loadMoreFn={loadMore}
            onFolderClickFn={handleFolderClick}
            onActionClick={handleActionClick}
            hiddenOptions={hiddenOptions}
            isMiniGallery={isMiniGallery}
            isSelectionEnabled={isSelectionEnabled}
            enableInfiniteLoading={enableInfiniteLoading}
            hardcodeSettings={hardcodeSettings}
          />
        )}
      </div>
    </div>
  );
}
